import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import api_cr = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_reply.alan_api;
import { serializeNumber, getDecimalsFromNumericalTypeProperties } from "../state 2 interface/serializeNumber";
import reQuery = require("../state 2 interface/reQuery");
import SubscriptionBuilder from "../state 2 interface/SubscriptionBuilder";
import * as mapToIdPathBuilder from "../state/mapToIdPathBuilder";
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import DestructorTree from "../DestructorTree";
import * as Cell from "../state/Cell";
import { getTranslation } from "./phrases";
import { entryPermissionChecked } from "../state 2 interface/permissions";

function walkNodePath(
	path: api_views.Cnode_path,
	on_great_succes: (build: () => SubscriptionBuilder, column_lookup: string[]) => void,
	build: () => SubscriptionBuilder,
	column_lookup: string[]
): void {
	path.properties.has_steps.switch({
		no: () => {
			on_great_succes(build, column_lookup);
		},
		yes: ($) => {
			const tail = $.properties.tail;
			$.properties.type.switch({
				parent: undefined,
				group: ($) => {
					const group_name = $.properties.group.ref.parent.parent.key.ref.key;
					walkNodePath(
						tail,
						on_great_succes,
						() => {
							const subscription_builder = build();
							return subscription_builder.group(group_name);
						},
						column_lookup.concat(group_name)
					);
				},
				reference: undefined,
				"reference rule": undefined,
				"state rule": undefined,
				state: ($) => {
					const state_group_name = $.properties.state_group.ref.parent.parent.key.ref.key;
					const state_name = $.properties.state.ref.key.ref.key;
					return walkNodePath(
						tail,
						on_great_succes,
						() => {
							const subscription_builder = build();
							return subscription_builder.state(
								state_group_name,
								state_name,
								$.properties.state_group.ref.inferences.state_group()
							);
						},
						column_lookup.concat([state_group_name, state_name])
					);
				},
				"collection entry": undefined,
			});
		},
	});
}

type ColumnLookup = {
	properties: Map<string, ColumnLookup>;
	columns: string[];
};

function AddColumnToLookup(current_lookup: ColumnLookup, column: string, column_properties: string[]) {
	for (let i = 0; i < column_properties.length; i++) {
		const column_property = column_properties[i];
		if (!current_lookup.properties.has(column_property)) {
			current_lookup.properties.set(column_property, { properties: new Map(), columns: [] });
		}
		current_lookup = current_lookup.properties.get(column_property)!;
	}
	current_lookup.columns.push(column);
	return current_lookup;
}

function setToIsSetNo(column_lookup: ColumnLookup, entry: api_cs.Centries__yes__has_result) {
	for (const prop of column_lookup.properties.values()) {
		setToIsSetNo(prop, entry);
	}
	column_lookup.columns.forEach(function (column_name) {
		const cell = entry.properties.cells.get(column_name)!;
		cell.properties.type.switch({
			file: function ($) {
				$.properties.is_set.set("no", {});
			},
			number: function ($) {
				$.properties.is_set.set("no", {});
			},
			"state group": function ($) {
				$.properties.is_set.set("no", {});
			},
			text: function ($) {
				$.properties.is_set.set("no", {});
			},
			widget: undefined,
		});
	});
}

function updateEntryBasedOnInitializationData(
	initializationData: api_cr.Csubscription_result_data,
	entry: api_cs.Centries__yes__has_result,
	column_lookup: ColumnLookup
) {
	switch (initializationData.properties.read_permitted.state.name) {
		case "no":
			setToIsSetNo(column_lookup, entry);
			break;
		case "yes": {
			const $ = initializationData.properties.read_permitted.state.node;
			for (const [attribute_name, read_permitted] of $.properties.attributes) {
				switch (read_permitted.properties.type.state.name) {
					case "command":
						break;
					case "property": {
						const property = read_permitted.properties.type.state.node;
						switch (property.properties.type.state.name) {
							case "text": {
								const $ = property.properties.type.state.node;
								for (const column_name of column_lookup.properties.get(attribute_name)!.columns) {
									const cell = entry.properties.cells.get(column_name)!.properties.type.cast("text");
									const text_value = $.properties.value;
									cell.properties.is_set.switch({
										no: function () {
											cell.properties.is_set.set("yes", {
												text: text_value,
											});
										},
										yes: function ($) {
											$.set_text(text_value);
										},
									});
								}
								break;
							}
							case "number": {
								const $ = property.properties.type.state.node;
								for (const column_name of column_lookup.properties.get(attribute_name)!.columns) {
									const cell = entry.properties.cells.get(column_name)!.properties.type.cast("number");
									const number_value = $.properties.value;
									cell.properties.is_set.set("yes", {
										number: number_value.value,
										"textual representation": serializeNumber(
											number_value.value,
											getDecimalsFromNumericalTypeProperties(cell.properties.numerical_type_properties),
											cell.inferences.column_type().inferences.number_type().properties.number.ref.properties
												.numerical_type.ref.key.ref
										),
									});
								}
								break;
							}
							case "file": {
								const $ = property.properties.type.state.node;
								for (const column_name of column_lookup.properties.get(attribute_name)!.columns) {
									const cell = entry.properties.cells.get(column_name)!.properties.type.cast("file");
									cell.properties.is_set.set("yes", {
										token: $.properties.token,
										extension: $.properties.extension,
									});
								}
								break;
							}
							case "group": {
								const $ = property.properties.type.state.node;
								updateEntryBasedOnInitializationData(
									$.properties.node_data,
									entry,
									column_lookup.properties.get(attribute_name)!
								);
								break;
							}
							case "state group": {
								const $ = property.properties.type.state.node;
								const state_name = $.properties.state.entry;
								for (const column_name of column_lookup.properties.get(attribute_name)!.columns) {
									const cell = entry.properties.cells.get(column_name)!.properties.type.cast("state group");
									Cell.updateStategroup(cell, getTranslation(state_name));
								}
								updateEntryBasedOnInitializationData(
									$.properties.node_data,
									entry,
									column_lookup.properties.get(attribute_name)!.properties.get(state_name)!
								);
								break;
							}
							case "collection":
								break;
							default:
								unexpectedState(property.properties.type.state);
						}
						break;
					}
					default:
						unexpectedState(read_permitted.properties.type.state);
				}
			}
			break;
		}
		default:
			unexpectedState(initializationData.properties.read_permitted.state);
	}
}

function updateEntryBasedOnNotificationData(
	notificationData: api_cr.Cnotification_data,
	entry: api_cs.Centries__yes__has_result,
	column_lookup: ColumnLookup
) {
	notificationData.properties.properties.forEach(function ($) {
		const property_name = $.key_value;
		$.properties.type.switch({
			collection: undefined,
			file: function ($) {
				column_lookup.properties.get(property_name)!.columns.forEach(function (column_name) {
					const cell = entry.properties.cells.get(column_name)!.properties.type.cast("file");
					cell.properties.is_set.set("yes", {
						token: $.properties.token,
						extension: $.properties.extension,
					});
				});
			},
			group: function ($) {
				updateEntryBasedOnNotificationData(
					$.properties.notification_data,
					entry,
					column_lookup.properties.get(property_name)!
				);
			},
			number: function ($) {
				column_lookup.properties.get(property_name)!.columns.forEach(function (column_name) {
					const cell = entry.properties.cells.get(column_name)!.properties.type.cast("number");
					const number_value = $.properties.value.value;
					cell.properties.is_set.set("yes", {
						number: number_value,
						"textual representation": serializeNumber(
							number_value,
							getDecimalsFromNumericalTypeProperties(cell.properties.numerical_type_properties),
							cell.inferences.column_type().inferences.number_type().properties.number.ref.properties.numerical_type.ref
								.key.ref
						),
					});
				});
			},
			"state group": function ($) {
				const state_name = $.properties.state.ref.key.ref.key;
				column_lookup.properties.get(property_name)!.columns.forEach(function (column_name) {
					const cell = entry.properties.cells.get(column_name)!.properties.type.cast("state group");
					Cell.updateStategroup(cell, getTranslation(state_name));
				});
				for (const [name, state] of column_lookup.properties.get(property_name)!.properties) {
					if (name !== state_name) {
						setToIsSetNo(state, entry);
					}
				}
				$.properties.type.switch({
					set: function ($) {
						updateEntryBasedOnInitializationData(
							$.properties.initialization_data,
							entry,
							column_lookup.properties.get(property_name)!.properties.get(state_name)!
						);
					},
					update: function ($) {
						updateEntryBasedOnNotificationData(
							$.properties.notification_data,
							entry,
							column_lookup.properties.get(property_name)!.properties.get(state_name)!
						);
					},
				});
			},
			text: function ($) {
				column_lookup.properties.get(property_name)!.columns.forEach(function (column_name) {
					$.properties.value_update.switch({
						no: undefined,
						yes: ($) => {
							const cell = entry.properties.cells.get(column_name)!.properties.type.cast("text");
							const text_value = $.properties.value;
							cell.properties.is_set.switch({
								no: function () {
									cell.properties.is_set.set("yes", {
										text: text_value,
									});
								},
								yes: function ($) {
									$.set_text(text_value);
								},
							});
						},
					});
				});
			},
		});
	});
}

export default function (entry: api_cs.Centries__yes__has_result, destructor: DestructorTree): void {
	const query = entry.parent.parent;
	let id_path: IdPathBuilder | undefined;
	const initialized = entry.properties.entry_status.properties.initialized.state;
	switch (initialized.name) {
		case "no":
			id_path = mapToIdPathBuilder.fromClientState(initialized.node.properties.referenced_node);
			break;
		case "yes":
			id_path = createIdPathBuilderFromNode(initialized.node.properties.referenced_node.output.result_node());
			break;
		case "unknown":
	}
	if (id_path === undefined) return;
	const subscriptionBuilder = SubscriptionBuilder.create();
	const column_lookup: ColumnLookup = { properties: new Map(), columns: [] };
	query.properties.definition.ref.properties.columns.forEach(($) => {
		const column_name = $.key;
		//Only add subscriptions to cells that do not contain out of context steps (like collection parent)
		walkNodePath(
			$.properties.path,
			(builder, column_properties) => {
				$.properties.type.switch({
					file: ($) => {
						const attribute = $.properties.file.entry;
						AddColumnToLookup(column_lookup, column_name, column_properties.concat(attribute));
						builder().file(attribute);
					},
					number: ($) => {
						const attribute = $.properties.number.entry;
						AddColumnToLookup(column_lookup, column_name, column_properties.concat(attribute));
						builder().number(attribute);
					},
					"state group": ($) => {
						const attribute = $.properties.state_group.entry;
						const state_group_lookup = AddColumnToLookup(
							column_lookup,
							column_name,
							column_properties.concat(attribute)
						);
						$.properties.state_group.ref.properties.states.forEach((state) => {
							if (!state_group_lookup.properties.has(state.key.entry)) {
								state_group_lookup.properties.set(state.key.entry, { properties: new Map(), columns: [] });
							}
						});
						builder().state_group(attribute, $.properties.state_group.ref.inferences.state_group());
					},
					text: ($) => {
						const attribute_name = $.properties.text.entry;
						AddColumnToLookup(column_lookup, column_name, column_properties.concat(attribute_name));
						$.properties.text.ref.properties.navigable.switch({
							no: () => builder().text(attribute_name),
							yes: () => builder().constrained_text(attribute_name),
						});
					},
					widget: undefined,
				});
			},
			() => subscriptionBuilder,
			[]
		);
	});
	if (shouldSubscribeEntry(entry)) {
		entryPermissionChecked(entry);
		const datastore_subscription = DatastoreProxy.instance.subscribe(
			{
				"context node": id_path.build(),
				"node subscription": {
					attributes: subscriptionBuilder.properties,
				},
				"mutation permissions": ["include", {}],
			},
			(result) => {
				if (entry.destroyed) {
					datastore_subscription.unsubscribe();
					return;
				}
				result.properties.context.switch<void>({
					"does not exist": () => entry.properties.is_editable.set("no", {}),
					exists: ($) =>
						$.properties.delete_permitted.properties.permitted.switch<void>({
							no: () => entry.properties.is_editable.set("no", {}),
							yes: () => entry.properties.is_editable.set("yes", {}),
						}),
					"permission denied": () => entry.properties.is_editable.set("no", {}),
				});
			},
			function (subscription_notification) {
				if (entry.destroyed) {
					return;
				}
				subscription_notification.properties.context.switch({
					exists: function ($) {
						const notificationData = $.properties.notification_data;
						updateEntryBasedOnNotificationData(notificationData, entry, column_lookup);
					},
					"permission denied": undefined,
					"does not exist": () => reQuery.query(query),
				});
			}
		);
		destructor.onDestroy(() => datastore_subscription.unsubscribe());
	}
}

function shouldSubscribeEntry(entry: api_cs.Centries__yes__has_result) {
	if ((entry.component_root as any)._refresh_interval === true) {
		return false;
	}
	switch (entry.properties.entry_status.properties.initialized.state.name) {
		case "no":
			break;
		case "yes": {
			const intialized = entry.properties.entry_status.properties.initialized.state.node;
			const node = intialized.properties.referenced_node.output.result_node();
			switch (node.input.location().variant.name) {
				case "local":
					return false;
				case "remote":
					break;
			}
			break;
		}
		case "unknown":
			break;
	}
	return true;
}
